class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.deliveryObserver = null;
    this.deliveryNormalizationFrame = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.observeDeliveryNodes();
    this.normalizeDeliveryNodes();
    this.style.visibility = 'visible';
    this.style.opacity = '1';
    this.style.pointerEvents = 'auto';
    this.setAttribute('aria-hidden', 'false');
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    this.style.opacity = '0';
    this.style.pointerEvents = 'none';
    this.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (!this.classList.contains('active')) this.style.visibility = 'hidden';
    }, 300);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.observeDeliveryNodes();
      this.normalizeDeliveryNodes();
      this.open();
    });
  }

  observeDeliveryNodes() {
    const cartItems = this.querySelector('#CartDrawer-CartItems');

    if (!cartItems) return;

    if (this.deliveryObserver) {
      this.deliveryObserver.disconnect();
    }

    this.deliveryObserver = new MutationObserver(() => this.scheduleDeliveryNormalization());
    this.deliveryObserver.observe(cartItems, { childList: true, subtree: true });
  }

  scheduleDeliveryNormalization() {
    if (this.deliveryNormalizationFrame) cancelAnimationFrame(this.deliveryNormalizationFrame);

    this.deliveryNormalizationFrame = requestAnimationFrame(() => {
      this.normalizeDeliveryNodes();
      this.deliveryNormalizationFrame = null;
    });
  }

  normalizeDeliveryNodes() {
    this.querySelectorAll('.cart-item__details').forEach((details) => {
      let deliveryNode = this.findDeliveryNode(details);

      while (deliveryNode) {
        deliveryNode.remove();
        deliveryNode = this.findDeliveryNode(details);
      }
    });
  }

  findDeliveryNode(details) {
    const candidates = [...details.querySelectorAll('*')].filter((element) => {
      if (['SCRIPT', 'STYLE', 'IMG', 'A'].includes(element.tagName)) return false;
      return this.isDeliveryText(this.normalizeText(element.textContent));
    });

    if (!candidates.length) return null;

    const matchedOption = candidates.find((element) => element.classList.contains('product-option'));
    if (matchedOption) return matchedOption;

    let node = candidates[0];

    while (node.parentElement && node.parentElement !== details) {
      const parent = node.parentElement;
      const parentText = this.normalizeText(parent.textContent);
      const nodeText = this.normalizeText(node.textContent);

      if (parent.tagName === 'DL') break;

      if (parent.children.length === 1 || parentText === nodeText) {
        node = parent;
        continue;
      }

      break;
    }

    return node;
  }

  isDeliveryText(text) {
    return /estimated delivery|made just for you|arrives by/i.test(text);
  }

  normalizeText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
